<template>
    <div class="flex mt-[42px] mb-10 justify-between">
        <h1
            class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
        >
            {{ $t("vue.welcome") }}, {{ userProps.name }}!
        </h1>

        <div>
            <div
                :class="[
                    'relative',
                    !hasDateRangeAccess && 'cursor-pointer opacity-50',
                ]"
                @click="!hasDateRangeAccess && checkAccessOrShowModal()"
            >
                <DateRangePicker
                    v-model="selectedDateRange"
                    :disabled="!hasDateRangeAccess"
                    :display-full-block="true"
                    @update:model-value="handleDateRangeChange"
                />
                <div
                    v-if="!hasDateRangeAccess"
                    class="absolute inset-0 bg-transparent cursor-pointer"
                    title="Click for upgrade options - Scale plan required"
                />
            </div>
        </div>
    </div>

    <div v-if="hasReportsAccess">
        <div
            v-if="
                globalProps.auth.user.current_team.id === 6 &&
                globalProps.auth.user.current_team.paddle_sub_active !== 1
            "
            class="mb-10 rounded-md border border-green-500 bg-green-50 p-4"
            style="background-color: #f0fdf4"
        >
            <div class="flex">
                <div class="shrink-0">
                    <svg
                        class="h-7 w-7 text-green-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </div>
                <div class="ml-3">
                    <h3 class="text-lg font-medium text-green-800">
                        {{
                            daysLeftToTrial(
                                globalProps.auth.user.current_team.created_at,
                            )
                        }}
                        {{ $t("vue.days_left_from_unlimited_trial") }}
                    </h3>
                    <div class="mt-2 text-sm text-green-700">
                        <p class="mb-2">
                            {{ $t("vue.enjoy_unlimited_trial") }}
                            <strong>60 {{ $t("vue.days") }}</strong
                            >—{{ $t("vue.simply_by_sharing_your_thoughts") }}
                        </p>
                        <p>
                            {{ $t("vue.after_trial_expires") }}
                            <strong>{{ $t("vue.free_plan") }}</strong
                            >, {{ $t("vue.which_includes") }}
                            <strong>{{
                                $t("vue.domains_and_messages_per_month")
                            }}</strong
                            >.
                        </p>
                    </div>
                    <div class="mt-4">
                        <div class="-mx-2 flex">
                            <a
                                href="https://www.g2.com/products/dmarceye/reviews"
                                target="_blank"
                                type="button"
                                class="rounded-md bg-green-200 px-2 py-1.5 text-sm font-medium text-green-800 hover:bg-green-300 focus:outline-hidden focus:ring-2 focus:ring-green-600 focus:ring-offset-2 focus:ring-offset-green-200"
                                >{{ $t("vue.write_g2_review") }}</a
                            >
                            <a
                                :href="route('billing.subscriptions.index')"
                                type="button"
                                class="ml-3 rounded-md bg-green-200 px-2 py-1.5 text-sm font-medium text-green-800 hover:bg-green-300 focus:outline-hidden focus:ring-2 focus:ring-green-600 focus:ring-offset-2 focus:ring-offset-green-200"
                                >{{ $t("vue.upgrade_your_plan") }}</a
                            >
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div>
            <StatsBar
                :stats="{ pass: totalPass, fail: totalFail }"
                :stats-before="{ pass: totalPassBefore, fail: totalFailBefore }"
                :statistics="props.statistics"
            />
        </div>

        <div v-if="props.statistics.totalDomains > 0">
            <div
                v-if="!aiDetailsOpened"
                class="mb-[21px] cursor-pointer rounded-lg border border-gray-200 bg-white p-4 transition-all duration-200 hover:shadow-md"
                @click="aiDetailsOpened = true"
            >
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3">
                        <div
                            class="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-brain h-4 w-4 text-white"
                                aria-hidden="true"
                            >
                                <path
                                    d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"
                                ></path>
                                <path
                                    d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"
                                ></path>
                                <path
                                    d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"
                                ></path>
                                <path
                                    d="M17.599 6.5a3 3 0 0 0 .399-1.375"
                                ></path>
                                <path
                                    d="M6.003 5.125A3 3 0 0 0 6.401 6.5"
                                ></path>
                                <path
                                    d="M3.477 10.896a4 4 0 0 1 .585-.396"
                                ></path>
                                <path
                                    d="M19.938 10.5a4 4 0 0 1 .585.396"
                                ></path>
                                <path d="M6 18a4 4 0 0 1-1.967-.516"></path>
                                <path d="M19.967 17.484A4 4 0 0 1 18 18"></path>
                            </svg>
                        </div>
                        <div>
                            <div class="flex items-center gap-2">
                                <span class="font-medium text-gray-900">{{
                                    $t("vue.ai_security_analysis")
                                }}</span>
                                <span
                                    data-slot="badge"
                                    class="w-fit [&amp;>svg]:size-3 [&amp;>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive [a&amp;]:hover:bg-accent [a&amp;]:hover:text-accent-foreground inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium text-blue-600 transition-[color,box-shadow] focus-visible:ring-[3px]"
                                    >{{ aiAssesment.status }}</span
                                >
                                <span
                                    data-slot="badge"
                                    class="w-fit [&amp;>svg]:size-3 [&amp;>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive text-secondary-foreground [a&amp;]:hover:bg-secondary/90 inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-transparent bg-secondary px-2 py-0.5 text-xs font-medium transition-[color,box-shadow] focus-visible:ring-[3px]"
                                    >{{ avgCompliance }}%
                                    {{ $t("vue.compliant") }}</span
                                >
                            </div>
                            <p class="text-sm text-gray-600">
                                <strong>{{ aiAssesment.result_title }}</strong>
                                {{ aiAssesment.result_short_assesment }}
                            </p>
                        </div>
                    </div>
                    <div class="flex items-center gap-2">
                        <span class="text-sm text-gray-500">{{
                            $t("vue.click_for_details")
                        }}</span>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-chevron-right h-4 w-4 text-gray-400"
                            aria-hidden="true"
                        >
                            <path d="m9 18 6-6-6-6"></path>
                        </svg>
                    </div>
                </div>
            </div>
            <div
                v-else
                class="mb-[21px] overflow-hidden rounded-xl border border-gray-200 bg-white"
            >
                <div
                    class="to-indigo-50 border-b border-gray-100 bg-linear-to-r from-blue-50 px-6 py-4"
                >
                    <div class="flex items-center justify-between">
                        <div class="flex items-center gap-3">
                            <div
                                class="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-brain h-5 w-5 text-white"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"
                                    ></path>
                                    <path
                                        d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"
                                    ></path>
                                    <path
                                        d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"
                                    ></path>
                                    <path
                                        d="M17.599 6.5a3 3 0 0 0 .399-1.375"
                                    ></path>
                                    <path
                                        d="M6.003 5.125A3 3 0 0 0 6.401 6.5"
                                    ></path>
                                    <path
                                        d="M3.477 10.896a4 4 0 0 1 .585-.396"
                                    ></path>
                                    <path
                                        d="M19.938 10.5a4 4 0 0 1 .585.396"
                                    ></path>
                                    <path d="M6 18a4 4 0 0 1-1.967-.516"></path>
                                    <path
                                        d="M19.967 17.484A4 4 0 0 1 18 18"
                                    ></path>
                                </svg>
                            </div>
                            <div>
                                <h3 class="font-medium text-gray-900">
                                    {{ $t("vue.ai_security_analysis") }}
                                </h3>
                                <p class="text-sm text-gray-600">
                                    {{
                                        $t(
                                            "vue.comprehensive_dmarc_intelligence",
                                        )
                                    }}
                                </p>
                            </div>
                        </div>
                        <button
                            data-slot="button"
                            class="[&amp;_svg]:pointer-events-none [&amp;_svg:not([class*='size-'])]:size-4 [&amp;_svg]:shrink-0 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive hover:bg-accent dark:hover:bg-accent/50 inline-flex h-8 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md px-3 text-sm font-medium text-gray-500 outline-hidden transition-all hover:text-gray-700 focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 has-[>svg]:px-2.5"
                            @click="aiDetailsOpened = false"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-chevron-down h-4 w-4"
                                aria-hidden="true"
                            >
                                <path d="m6 9 6 6 6-6"></path>
                            </svg>
                            {{ $t("vue.collapse") }}
                        </button>
                    </div>
                </div>
                <div class="p-6">
                    <div
                        class="mb-8 rounded-lg border border-blue-200 bg-blue-50 p-4"
                    >
                        <div class="flex items-start gap-4">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-circle-check-big mt-0.5 h-6 w-6 shrink-0 text-blue-600"
                                aria-hidden="true"
                            >
                                <path
                                    d="M21.801 10A10 10 0 1 1 17 3.335"
                                ></path>
                                <path d="m9 11 3 3L22 4"></path>
                            </svg>
                            <div class="flex-1">
                                <div class="mb-2 flex items-center gap-3">
                                    <h4 class="font-medium text-blue-600">
                                        {{ aiAssesment.result_title }}
                                    </h4>
                                    <span
                                        data-slot="badge"
                                        class="w-fit [&amp;>svg]:size-3 [&amp;>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive text-foreground [a&amp;]:hover:bg-accent [a&amp;]:hover:text-accent-foreground inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium transition-[color,box-shadow] focus-visible:ring-[3px]"
                                        >97.8% {{ $t("vue.compliant") }}</span
                                    >
                                </div>
                                <p class="text-sm text-blue-700">
                                    {{ aiAssesment.result_short_assesment }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div
                        class="mt-8 rounded-lg border border-blue-100 bg-blue-50 p-4"
                    >
                        <div class="flex items-start gap-4">
                            <div
                                class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-100"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-brain h-5 w-5 text-blue-600"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"
                                    ></path>
                                    <path
                                        d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"
                                    ></path>
                                    <path
                                        d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"
                                    ></path>
                                    <path
                                        d="M17.599 6.5a3 3 0 0 0 .399-1.375"
                                    ></path>
                                    <path
                                        d="M6.003 5.125A3 3 0 0 0 6.401 6.5"
                                    ></path>
                                    <path
                                        d="M3.477 10.896a4 4 0 0 1 .585-.396"
                                    ></path>
                                    <path
                                        d="M19.938 10.5a4 4 0 0 1 .585.396"
                                    ></path>
                                    <path d="M6 18a4 4 0 0 1-1.967-.516"></path>
                                    <path
                                        d="M19.967 17.484A4 4 0 0 1 18 18"
                                    ></path>
                                </svg>
                            </div>
                            <div class="flex-1">
                                <h5 class="mb-2 mt-1 font-medium text-blue-900">
                                    {{ $t("vue.ai_summary") }}
                                </h5>
                                <div
                                    class="text-md mb-3 text-sm leading-8 text-blue-800"
                                    v-html="aiAssesment.result_long_assesment"
                                ></div>
                            </div>
                        </div>
                    </div>

                    <div
                        class="mt-8 rounded-lg border border-yellow-100 bg-yellow-50 p-4"
                    >
                        <div class="flex items-start gap-4">
                            <div
                                class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-yellow-100"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-brain h-5 w-5 text-yellow-600"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"
                                    ></path>
                                    <path
                                        d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"
                                    ></path>
                                    <path
                                        d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"
                                    ></path>
                                    <path
                                        d="M17.599 6.5a3 3 0 0 0 .399-1.375"
                                    ></path>
                                    <path
                                        d="M6.003 5.125A3 3 0 0 0 6.401 6.5"
                                    ></path>
                                    <path
                                        d="M3.477 10.896a4 4 0 0 1 .585-.396"
                                    ></path>
                                    <path
                                        d="M19.938 10.5a4 4 0 0 1 .585.396"
                                    ></path>
                                    <path d="M6 18a4 4 0 0 1-1.967-.516"></path>
                                    <path
                                        d="M19.967 17.484A4 4 0 0 1 18 18"
                                    ></path>
                                </svg>
                            </div>
                            <div class="flex-1">
                                <h5
                                    class="mb-2 mt-1 font-medium text-yellow-900"
                                >
                                    {{ $t("vue.ai_recommendations") }}
                                </h5>
                                <div
                                    class="text-md mb-3 text-sm leading-8 text-yellow-800"
                                    v-html="aiAssesment.recommendations"
                                ></div>
                            </div>
                        </div>
                    </div>

                    <div class="grid hidden grid-cols-1 gap-8 lg:grid-cols-2">
                        <div>
                            <h4
                                class="mb-4 flex items-center gap-2 font-medium text-gray-900"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-trending-up h-4 w-4 text-blue-600"
                                    aria-hidden="true"
                                >
                                    <path d="M16 7h6v6"></path>
                                    <path d="m22 7-8.5 8.5-5-5L2 17"></path>
                                </svg>
                                {{ $t("vue.detailed_analysis") }}
                            </h4>
                            <div class="space-y-4">
                                <div class="rounded-lg bg-gray-50 p-4">
                                    <div class="flex items-start gap-3">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-circle-check-big mt-0.5 h-5 w-5 text-green-600"
                                            aria-hidden="true"
                                        >
                                            <path
                                                d="M21.801 10A10 10 0 1 1 17 3.335"
                                            ></path>
                                            <path d="m9 11 3 3L22 4"></path>
                                        </svg>
                                        <div class="flex-1">
                                            <div
                                                class="mb-1 flex items-center gap-3"
                                            >
                                                <h5
                                                    class="font-medium text-gray-900"
                                                >
                                                    DMARC Compliance Rate
                                                </h5>
                                                <span
                                                    data-slot="badge"
                                                    class="w-fit [&amp;>svg]:size-3 [&amp;>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive text-foreground [a&amp;]:hover:bg-accent [a&amp;]:hover:text-accent-foreground inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium transition-[color,box-shadow] focus-visible:ring-[3px]"
                                                    >{{ avgCompliance }}%</span
                                                >
                                            </div>
                                            <p
                                                class="mb-1 text-sm text-gray-600"
                                            >
                                                {{
                                                    new Intl.NumberFormat().format(
                                                        totalPass,
                                                    )
                                                }}
                                                out of
                                                {{
                                                    new Intl.NumberFormat().format(
                                                        totalPass + totalFail,
                                                    )
                                                }}
                                                emails passed authentication
                                            </p>
                                            <p
                                                class="hidden text-xs text-gray-500"
                                            >
                                                +2.3% from last month (TODO)
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div class="rounded-lg bg-gray-50 p-4">
                                    <div class="flex items-start gap-3">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-info mt-0.5 h-5 w-5 text-blue-600"
                                            aria-hidden="true"
                                        >
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="10"
                                            ></circle>
                                            <path d="M12 16v-4"></path>
                                            <path d="M12 8h.01"></path>
                                        </svg>
                                        <div class="flex-1">
                                            <div
                                                class="mb-1 flex items-center gap-3"
                                            >
                                                <h5
                                                    class="font-medium text-gray-900"
                                                >
                                                    Authentication Failures
                                                </h5>
                                                <span
                                                    data-slot="badge"
                                                    class="w-fit [&amp;>svg]:size-3 [&amp;>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive text-foreground [a&amp;]:hover:bg-accent [a&amp;]:hover:text-accent-foreground inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium transition-[color,box-shadow] focus-visible:ring-[3px]"
                                                    >{{
                                                        avgNonCompliance
                                                    }}%</span
                                                >
                                            </div>
                                            <p
                                                class="mb-1 text-sm text-gray-600"
                                            >
                                                {{
                                                    new Intl.NumberFormat().format(
                                                        totalFail,
                                                    )
                                                }}
                                                emails failed DMARC checks
                                            </p>
                                            <p
                                                class="hidden text-xs text-gray-500"
                                            >
                                                -1.2% from last month (TODO)
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div class="rounded-lg bg-gray-50 p-4">
                                    <div class="flex items-start gap-3">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-globe mt-0.5 h-5 w-5 text-blue-600"
                                            aria-hidden="true"
                                        >
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="10"
                                            ></circle>
                                            <path
                                                d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"
                                            ></path>
                                            <path d="M2 12h20"></path>
                                        </svg>
                                        <div class="flex-1">
                                            <div
                                                class="mb-1 flex items-center gap-3"
                                            >
                                                <h5
                                                    class="font-medium text-gray-900"
                                                >
                                                    Domain Coverage
                                                </h5>
                                                <span
                                                    data-slot="badge"
                                                    class="w-fit [&amp;>svg]:size-3 [&amp;>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive text-foreground [a&amp;]:hover:bg-accent [a&amp;]:hover:text-accent-foreground inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium transition-[color,box-shadow] focus-visible:ring-[3px]"
                                                    >{{
                                                        activeDomains.length
                                                    }}/{{
                                                        activeDomains.length +
                                                        inactiveDomains.length +
                                                        notExistingDomains.length
                                                    }}</span
                                                >
                                            </div>
                                            <p
                                                class="mb-1 text-sm text-gray-600"
                                            >
                                                {{ activeDomains.length }}
                                                domains actively reporting,
                                                {{ inactiveDomains.length }}
                                                pending,
                                                {{ notExistingDomains.length }}
                                                not existing
                                            </p>
                                            <p
                                                class="hidden text-xs text-gray-500"
                                            >
                                                New domains added
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div class="hidden rounded-lg bg-gray-50 p-4">
                                    <div class="flex items-start gap-3">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-calendar mt-0.5 h-5 w-5 text-blue-600"
                                            aria-hidden="true"
                                        >
                                            <path d="M8 2v4"></path>
                                            <path d="M16 2v4"></path>
                                            <rect
                                                width="18"
                                                height="18"
                                                x="3"
                                                y="4"
                                                rx="2"
                                            ></rect>
                                            <path d="M3 10h18"></path>
                                        </svg>
                                        <div class="flex-1">
                                            <div
                                                class="mb-1 flex items-center gap-3"
                                            >
                                                <h5
                                                    class="font-medium text-gray-900"
                                                >
                                                    Reporting Frequency
                                                </h5>
                                                <span
                                                    data-slot="badge"
                                                    class="w-fit [&amp;>svg]:size-3 [&amp;>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive text-foreground [a&amp;]:hover:bg-accent [a&amp;]:hover:text-accent-foreground inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium transition-[color,box-shadow] focus-visible:ring-[3px]"
                                                    >24h</span
                                                >
                                            </div>
                                            <p
                                                class="mb-1 text-sm text-gray-600"
                                            >
                                                Average time between DMARC
                                                reports
                                            </p>
                                            <p class="text-xs text-gray-500">
                                                Consistent reporting
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="hidden">
                            <h4
                                class="mb-4 flex items-center gap-2 font-medium text-gray-900"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-shield h-4 w-4 text-blue-600"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
                                    ></path>
                                </svg>
                                Action Plan
                            </h4>
                            <div class="space-y-4">
                                <div class="rounded-lg bg-gray-50 p-4">
                                    <div class="flex items-start gap-3">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-shield mt-0.5 h-5 w-5 text-gray-600"
                                            aria-hidden="true"
                                        >
                                            <path
                                                d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
                                            ></path>
                                        </svg>
                                        <div class="flex-1">
                                            <div
                                                class="mb-2 flex items-center gap-2"
                                            >
                                                <h5
                                                    class="font-medium text-gray-900"
                                                >
                                                    Upgrade DMARC Policies
                                                </h5>
                                                <span
                                                    class="rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700"
                                                    >HIGH</span
                                                >
                                            </div>
                                            <p
                                                class="mb-2 text-sm text-gray-600"
                                            >
                                                Move from "None" to "Quarantine"
                                                policy for 6 domains to enhance
                                                protection
                                            </p>
                                            <div
                                                class="flex items-center gap-4 text-xs text-gray-500"
                                            >
                                                <span
                                                    >Impact: High security
                                                    improvement</span
                                                >
                                                <span>•</span>
                                                <span>Timeline: 1-2 weeks</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="rounded-lg bg-gray-50 p-4">
                                    <div class="flex items-start gap-3">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-globe mt-0.5 h-5 w-5 text-gray-600"
                                            aria-hidden="true"
                                        >
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="10"
                                            ></circle>
                                            <path
                                                d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"
                                            ></path>
                                            <path d="M2 12h20"></path>
                                        </svg>
                                        <div class="flex-1">
                                            <div
                                                class="mb-2 flex items-center gap-2"
                                            >
                                                <h5
                                                    class="font-medium text-gray-900"
                                                >
                                                    Configure Pending Domains
                                                </h5>
                                                <span
                                                    class="rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-700"
                                                    >MEDIUM</span
                                                >
                                            </div>
                                            <p
                                                class="mb-2 text-sm text-gray-600"
                                            >
                                                3 domains need DMARC record
                                                configuration
                                            </p>
                                            <div
                                                class="flex items-center gap-4 text-xs text-gray-500"
                                            >
                                                <span
                                                    >Impact: Complete
                                                    coverage</span
                                                ><span>•</span
                                                ><span>Timeline: 2-3 days</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="rounded-lg bg-gray-50 p-4">
                                    <div class="flex items-start gap-3">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-trending-up mt-0.5 h-5 w-5 text-gray-600"
                                            aria-hidden="true"
                                        >
                                            <path d="M16 7h6v6"></path>
                                            <path
                                                d="m22 7-8.5 8.5-5-5L2 17"
                                            ></path>
                                        </svg>
                                        <div class="flex-1">
                                            <div
                                                class="mb-2 flex items-center gap-2"
                                            >
                                                <h5
                                                    class="font-medium text-gray-900"
                                                >
                                                    Monitor Alignment Issues
                                                </h5>
                                                <span
                                                    class="rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700"
                                                    >LOW</span
                                                >
                                            </div>
                                            <p
                                                class="mb-2 text-sm text-gray-600"
                                            >
                                                Set up alerts for SPF/DKIM
                                                alignment failures
                                            </p>
                                            <div
                                                class="flex items-center gap-4 text-xs text-gray-500"
                                            >
                                                <span
                                                    >Impact: Proactive
                                                    monitoring</span
                                                ><span>•</span
                                                ><span>Timeline: 1 day</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mb-[21px] grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div
                    class="rounded-xl border border-gray-200 bg-white p-[21px]"
                >
                    <h3 class="mb-6 font-medium text-gray-900">
                        {{ $t("vue.dashboard_security_recommendations") }}
                    </h3>
                    <div class="space-y-4">
                        <div
                            v-if="
                                nonePoliciesDomains.length === 0 &&
                                inactiveDomains.length === 0
                            "
                            class="flex items-start gap-4 rounded-lg border border-green-100 bg-green-50 p-4"
                        >
                            <div
                                class="mt-2 h-2 w-2 shrink-0 rounded-full bg-green-500"
                            ></div>
                            <div>
                                <p class="mb-1 font-medium text-green-900">
                                    {{
                                        $t(
                                            "vue.dashboard_security_recommendations_all_looking_good",
                                        )
                                    }}
                                </p>
                                <p class="text-sm text-green-800">
                                    {{
                                        $t(
                                            "vue.dashboard_security_recommendations_we_have",
                                        )
                                    }}
                                </p>
                            </div>
                        </div>

                        <div
                            v-if="nonePoliciesDomains.length > 0"
                            class="flex items-start gap-4 rounded-lg border border-yellow-100 bg-yellow-50 p-4"
                        >
                            <div
                                class="mt-2 h-2 w-2 shrink-0 rounded-full bg-yellow-500"
                            ></div>
                            <div>
                                <p class="mb-1 font-medium text-yellow-900">
                                    {{ $t("vue.upgrade_dmarc_policy") }}
                                </p>
                                <p class="text-sm text-yellow-800">
                                    {{
                                        $t("vue.upgrade_dmarc_policy_desc", {
                                            count:
                                                nonePoliciesDomains.length ?? 0,
                                        })
                                    }}
                                </p>
                            </div>
                        </div>

                        <div
                            v-if="noneDmarcRecordDomains.length > 0"
                            class="flex items-start gap-4 rounded-lg border border-blue-100 bg-blue-50 p-4"
                        >
                            <div
                                class="mt-2 h-2 w-2 shrink-0 rounded-full bg-blue-500"
                            ></div>
                            <div>
                                <p class="mb-1 font-medium text-blue-900">
                                    {{ $t("vue.monitor_new_domains") }}
                                </p>
                                <p
                                    v-if="noneDmarcRecordDomains.length > 1"
                                    class="text-sm text-blue-800"
                                >
                                    {{
                                        $t(
                                            "vue.monitor_new_domains_desc_waiting",
                                            {
                                                count:
                                                    noneDmarcRecordDomains.length ??
                                                    0,
                                            },
                                        )
                                    }}
                                </p>
                                <p v-else class="text-sm text-blue-800">
                                    {{
                                        $t(
                                            "vue.monitor_new_domains_desc_waiting_singular",
                                        )
                                    }}
                                </p>
                            </div>
                        </div>

                        <div
                            v-if="inactiveDomains.length > 0"
                            class="flex items-start gap-4 rounded-lg border border-blue-100 bg-white-50 p-4"
                        >
                            <div
                                class="mt-2 h-2 w-2 shrink-0 rounded-full bg-blue-500"
                            ></div>
                            <div>
                                <p class="mb-1 font-medium text-blue-900">
                                    {{ $t("vue.most_active_domains") }}
                                </p>
                                <p
                                    v-if="inactiveDomains.length > 1"
                                    class="text-sm text-blue-800"
                                >
                                    {{
                                        $t(
                                            "vue.most_active_domains_desc_inactive",
                                            {
                                                count:
                                                    inactiveDomains.length ?? 0,
                                            },
                                        )
                                    }}
                                </p>
                                <p v-else class="text-sm text-blue-800">
                                    {{
                                        $t(
                                            "vue.most_active_domains_desc_inactive_singular",
                                            {
                                                count:
                                                    inactiveDomains.length ?? 0,
                                            },
                                        )
                                    }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    class="rounded-xl border border-gray-200 bg-white p-[21px]"
                >
                    <h3 class="mb-6 font-medium text-gray-900">
                        {{ $t("vue.quick_stats") }}
                    </h3>
                    <div class="space-y-4">
                        <div class="flex items-center justify-between py-2">
                            <span class="text-gray-600">{{
                                $t("vue.active_domains")
                            }}</span>
                            <span class="font-medium text-gray-900">{{
                                props.statistics.activeDomains
                            }}</span>
                        </div>
                        <div class="flex items-center justify-between py-2">
                            <span class="text-gray-600">{{
                                $t("vue.average_compliance")
                            }}</span>
                            <span
                                class="font-medium"
                                :style="{
                                    color: calculateColorByPercents(
                                        avgCompliance + '%',
                                    ),
                                }"
                                >{{ avgCompliance }}%</span
                            >
                        </div>
                        <div class="flex items-center justify-between py-2">
                            <span class="text-gray-600">{{
                                $t("vue.policy_coverage")
                            }}</span>
                            <!--                    <span class="font-medium text-gray-900"-->
                            <!--                    >{{-->
                            <!--                            Math.round(-->
                            <!--                                (activeDomains.length /-->
                            <!--                                    (activeDomains.length +-->
                            <!--                                        inactiveDomains.length +-->
                            <!--                                        notExistingDomains.length)) *-->
                            <!--                                1000,-->
                            <!--                            ) / 10-->
                            <!--                        }}%</span-->
                            <!--                    >-->
                            <span class="font-medium text-gray-900">
                                {{
                                    Number(
                                        (
                                            props.statistics?.policyCoverage ??
                                            0
                                        ).toFixed(1),
                                    )
                                }}%
                            </span>
                        </div>
                        <div
                            class="flex hidden items-center justify-between py-2"
                        >
                            <span class="text-gray-600">{{
                                $t("vue.last_report")
                            }}</span>
                            <span class="font-medium text-gray-900"
                                >2 {{ $t("vue.hours_ago") }}</span
                            >
                        </div>
                    </div>
                </div>
            </div>

            <div
                class="overflow-hidden rounded-xl border border-gray-200 bg-white mb-40"
            >
                <div class="border-b border-gray-100 px-6 py-5">
                    <h3 class="font-medium text-gray-900">
                        {{ $t("vue.domain_overview_dashboard") }}
                    </h3>
                    <p class="mt-1 text-sm text-gray-600">
                        {{ $t("vue.domain_overview_dashboard_subheader") }}
                    </p>
                </div>
                <div class="overflow-x-auto">
                    <table class="w-full">
                        <thead class="border-b border-gray-100 bg-gray-50">
                            <tr>
                                <th
                                    class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                                >
                                    {{ $t("vue.dashboard_table_domain") }}
                                </th>
                                <th
                                    class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                                >
                                    {{ $t("vue.health") }}
                                </th>
                                <th
                                    class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                                >
                                    {{
                                        $t(
                                            "vue.dashboard_table_emails_reported",
                                        )
                                    }}
                                </th>
                                <th
                                    class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                                >
                                    {{
                                        $t(
                                            "vue.dashboard_table_dmarc_compliance",
                                        )
                                    }}
                                </th>
                                <th
                                    class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                                >
                                    {{
                                        $t("vue.dashboard_table_current_policy")
                                    }}
                                </th>
                            </tr>
                        </thead>

                        <tbody class="divide-y divide-gray-100">
                            <template
                                v-for="(domainList, group) in domainTableGroups"
                            >
                                <tr
                                    v-for="(domain, index) in domainList"
                                    :key="domain.domain"
                                    class="cursor-pointer transition-colors duration-150 hover:bg-gray-50"
                                    @click="
                                        $inertia.visit(
                                            route('reports.show', {
                                                domain: domain.domain,
                                            }),
                                        )
                                    "
                                >
                                    <td class="px-6 py-4">
                                        <div class="flex items-center gap-3">
                                            <div
                                                v-if="group === 'active'"
                                                class="h-2 w-2 rounded-full bg-green-500"
                                            ></div>
                                            <div
                                                v-else
                                                class="h-2 w-2 rounded-full bg-gray-400"
                                            ></div>
                                            <span
                                                class="font-medium text-blue-600 transition-colors duration-150 hover:text-blue-800 group-hover/domain:underline"
                                            >
                                                {{ domain.domain }}
                                            </span>
                                            <span
                                                v-if="group === 'inactive'"
                                                data-slot="badge"
                                                class="w-fit [&amp;>svg]:size-3 [&amp;>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive text-secondary-foreground [a&amp;]:hover:bg-secondary/90 inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-transparent bg-secondary px-2 py-0.5 text-xs font-medium transition-[color,box-shadow] focus-visible:ring-[3px]"
                                            >
                                                inactive
                                            </span>
                                            <span
                                                v-if="group === 'notExisting'"
                                                data-slot="badge"
                                                class="w-fit [&amp;>svg]:size-3 [&amp;>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive text-secondary-foreground [a&amp;]:hover:bg-secondary/90 inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-transparent bg-secondary px-2 py-0.5 text-xs font-medium transition-[color,box-shadow] focus-visible:ring-[3px]"
                                            >
                                                not existing
                                            </span>
                                        </div>
                                    </td>

                                    <td class="px-6 py-4">
                                        <svg
                                            v-if="
                                                parseInt(
                                                    calculateCompliance(
                                                        getCurrentReport(domain)
                                                            .pass,
                                                        getCurrentReport(domain)
                                                            .fail,
                                                    ),
                                                ) > 80
                                            "
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-circle-check-big h-4 w-4 text-green-500"
                                            aria-hidden="true"
                                        >
                                            <path
                                                d="M21.801 10A10 10 0 1 1 17 3.335"
                                            ></path>
                                            <path d="m9 11 3 3L22 4"></path>
                                        </svg>
                                        <svg
                                            v-else-if="
                                                parseInt(
                                                    calculateCompliance(
                                                        getCurrentReport(domain)
                                                            .pass,
                                                        getCurrentReport(domain)
                                                            .fail,
                                                    ),
                                                ) > 50
                                            "
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-circle-alert h-4 w-4 text-yellow-500"
                                            aria-hidden="true"
                                        >
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="10"
                                            ></circle>
                                            <line
                                                x1="12"
                                                x2="12"
                                                y1="8"
                                                y2="12"
                                            ></line>
                                            <line
                                                x1="12"
                                                x2="12.01"
                                                y1="16"
                                                y2="16"
                                            ></line>
                                        </svg>
                                        <svg
                                            v-else
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-circle-x h-4 w-4 text-red-500"
                                            aria-hidden="true"
                                        >
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="10"
                                            ></circle>
                                            <path d="m15 9-6 6"></path>
                                            <path d="m9 9 6 6"></path>
                                        </svg>
                                    </td>

                                    <template
                                        v-if="
                                            getCurrentReport(domain).pass > 0 ||
                                            getCurrentReport(domain).fail > 0
                                        "
                                    >
                                        <td class="px-6 py-4">
                                            <span class="text-gray-900">{{
                                                new Intl.NumberFormat().format(
                                                    getCurrentReport(domain)
                                                        .fail +
                                                        getCurrentReport(domain)
                                                            .pass,
                                                )
                                            }}</span>
                                        </td>
                                        <td class="px-6 py-4">
                                            <div
                                                class="flex items-center gap-3"
                                            >
                                                <div class="max-w-24 flex-1">
                                                    <div
                                                        class="h-2 w-full rounded-full bg-gray-200"
                                                    >
                                                        <div
                                                            class="h-2 rounded-full bg-green-500 transition-all duration-300"
                                                            :style="{
                                                                width: calculateCompliance(
                                                                    getCurrentReport(
                                                                        domain,
                                                                    ).pass,
                                                                    getCurrentReport(
                                                                        domain,
                                                                    ).fail,
                                                                ),
                                                                color: calculateColorByPercents(
                                                                    calculateCompliance(
                                                                        getCurrentReport(
                                                                            domain,
                                                                        ).pass,
                                                                        getCurrentReport(
                                                                            domain,
                                                                        ).fail,
                                                                    ),
                                                                ),
                                                            }"
                                                        ></div>
                                                    </div>
                                                </div>
                                                <span
                                                    class="text-sm font-medium text-green-600"
                                                    >{{
                                                        calculateCompliance(
                                                            getCurrentReport(
                                                                domain,
                                                            ).pass,
                                                            getCurrentReport(
                                                                domain,
                                                            ).fail,
                                                        )
                                                    }}</span
                                                >
                                            </div>
                                        </td>
                                        <td class="px-6 py-4 text-left">
                                            <span
                                                v-if="
                                                    domain.dmarcPolicy ===
                                                    'reject'
                                                "
                                                data-slot="badge"
                                                class="w-fit focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive [a&]:hover:bg-primary/90 inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-[#161E2E] bg-[#161E2E] px-2 py-0.5 text-xs font-medium text-white transition-[color,box-shadow] hover:bg-green-100 focus-visible:ring-[3px] [&>svg]:pointer-events-none [&>svg]:size-3"
                                                >Reject</span
                                            >
                                            <span
                                                v-else-if="
                                                    domain.dmarcPolicy ===
                                                    'quarantine'
                                                "
                                                data-slot="badge"
                                                class="w-fit focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive [a&]:hover:bg-primary/90 inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-[#FFF9E9] bg-[#FFF9E9] px-2 py-0.5 text-xs font-medium text-[#F0AD4E] transition-[color,box-shadow] hover:bg-blue-100 focus-visible:ring-[3px] [&>svg]:pointer-events-none [&>svg]:size-3"
                                                >Quarantine</span
                                            >
                                            <span
                                                v-else
                                                data-slot="badge"
                                                class="w-fit [&amp;>svg]:size-3 [&amp;>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive text-secondary-foreground [a&amp;]:hover:bg-secondary/90 inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-transparent bg-secondary px-2 py-0.5 text-xs font-medium transition-[color,box-shadow] focus-visible:ring-[3px]"
                                                >None</span
                                            >
                                        </td>
                                    </template>
                                    <template v-else>
                                        <!-- Emails Reported -->
                                        <td class="px-6 py-4">
                                            <span class="text-gray-900">0</span>
                                        </td>

                                        <!-- DMARC Compliance -->
                                        <td class="px-6 py-4">
                                            <span
                                                class="text-sm font-medium text-gray-500"
                                                >0%</span
                                            >
                                        </td>

                                        <!-- Current Policy -->
                                        <td class="px-6 py-4 text-left">
                                            <span
                                                data-slot="badge"
                                                class="w-fit inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-transparent bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground transition-[color,box-shadow] focus-visible:ring-[3px]"
                                                >None</span
                                            >
                                        </td>
                                    </template>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        <div v-else>
            <InfoCard
                :img-src="dataGraphSvg"
                img-alt="No data available"
                :title="t('vue.none_domains_yet')"
                :body-text="t('vue.none_domains_yet_instruction')"
                :show-dot="false"
            >
                <template #actions>
                    <AtomicButton
                        color="yellow"
                        size="lg"
                        @click="handleAddDomain"
                    >
                        {{ t("vue.add_new_domain") }}
                    </AtomicButton>
                </template>
            </InfoCard>
        </div>
    </div>

    <div v-else>
        <InfoCard
            :img-src="planCalendarSvg"
            img-alt="Trial ended"
            :title="t('vue.your_trial_ended')"
            :body-text="t('vue.your_trial_ended_action_step')"
            :emphasis-text="t('vue.your_trial_ended_action_step_continue')"
            :button-label="t('vue.choose_dmarceye_plan')"
            :button-href="route('billing.subscriptions.index')"
        />
    </div>

    <CreateDomainModal
        v-if="createDomainModal"
        @close="closeCreateDomainModal"
        @created="onDomainCreated"
    />

    <DomainLimitModal
        :show="showDomainLimitModal"
        :message="domainLimitMessage"
        @close="closeDomainLimitModal"
    />

    <TrialEndedPopup />
</template>

<script lang="ts" setup>
import { computed, type PropType, ref, onMounted } from "vue";
import type IDomain from "@/types/Domains/IDomain";
import type IUser from "@/types/Members/IUser";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/IRhizomPageProps";
import route from "ziggy-js";
import StatsBar from "@/Pages/Reports/components/StatsBar.vue";
import DateRangePicker from "@/Pages/Reports/components/DateRangePicker.vue";
import useDateRangeAccess from "@/composables/useDateRangeAccess.ts";
import { parseDate } from "@internationalized/date";
import { router, usePage } from "@inertiajs/vue3";
import type { DateRange } from "reka-ui";
import { PlusCircleIcon } from "@heroicons/vue/24/outline";
import CreateDomainModal from "@/Pages/Domains/components/CreateDomainModal.vue";
import { useTranslations } from "@/composables/useTranslations.ts";
import useReportsAccess from "@/composables/useReportsAccess.ts";
import DomainLimitModal from "@/Pages/Domains/components/DomainLimitModal.vue";
import DomainsOverview from "@/Pages/Domains/components/DomainsOverview.vue";
import InfoCard from "@/components/Content/InfoCard.vue";

const { t } = useTranslations();

const { hasReportsAccess } = useReportsAccess();

const DOMAIN_TABLE_LIMIT = 5;

const dataGraphSvg = new URL(
    "/resources/js/assets/images/data-graph.svg",
    import.meta.url,
).href;

const planCalendarSvg = new URL(
    "/resources/js/assets/images/plan-calendar.svg",
    import.meta.url,
).href;

type DomainGroupKey = "active" | "inactive" | "notExisting";

const domainTableGroups = computed<Record<DomainGroupKey, IDomain[]>>(() => {
    const rows: Array<{ group: DomainGroupKey; domain: IDomain }> = [
        ...props.activeDomains.map((domain) => ({ group: "active", domain })),
        ...props.inactiveDomains.map((domain) => ({
            group: "inactive",
            domain,
        })),
        ...props.notExistingDomains.map((domain) => ({
            group: "notExisting",
            domain,
        })),
    ].slice(0, DOMAIN_TABLE_LIMIT);

    const grouped: Record<DomainGroupKey, IDomain[]> = {
        active: [],
        inactive: [],
        notExisting: [],
    };

    for (const row of rows) {
        grouped[row.group].push(row.domain);
    }

    return grouped;
});

const globalProps = computed(() => usePage<IRhizomPageProps>().props);
const userProps = computed(() => globalProps.value.user as IUser);
const aiDetailsOpened = ref(false);
const aiAssesment = ref<any>({});

const props = defineProps({
    activeDomains: { type: Array as PropType<IDomain[]>, default: () => [] },
    inactiveDomains: { type: Array as PropType<IDomain[]>, default: () => [] },
    notExistingDomains: {
        type: Array as PropType<IDomain[]>,
        default: () => [],
    },

    statistics: {
        type: Object as PropType<{
            totalEmailsReported: number;
            dmarcCompliant: number;
            dmarcNonCompliant: number;
            complianceRate: number;
            totalDomains?: number;
            policyCoverage?: number;
        }>,
        required: true,
    },

    dateRange: {
        type: Object as PropType<{ start: string; end: string }>,
        required: true,
    },
});

type DomainReport = {
    pass: number;
    fail: number;
    sum?: number;
};

const getCurrentReport = (domain: any): DomainReport => {
    return (
        domain.report_current ??
        domain.report_30 ?? { pass: 0, fail: 0, sum: 0 }
    );
};

const getCurrentSum = (domain: any) => {
    const r = getCurrentReport(domain);
    return (r.sum ?? r.pass + r.fail) || 0;
};

const getReportBefore = (domain: IDomain): DomainReport => {
    return (
        (domain as any).report_before ??
        (domain as any).report_30_before ?? { pass: 0, fail: 0 }
    );
};

const {
    hasDateRangeAccess,
    showUpgradeModal,
    checkAccessOrShowModal,
    closeUpgradeModal,
} = useDateRangeAccess();

// Initialize date range from backend data for the picker v-model
const selectedDateRange = ref<DateRange>({
    start: parseDate(props.dateRange.start),
    end: parseDate(props.dateRange.end),
});

// Handle date range change from picker and refresh dashboard data
const handleDateRangeChange = (newRange: DateRange) => {
    if (!checkAccessOrShowModal()) {
        return;
    }

    if (!newRange?.start || !newRange?.end) {
        return;
    }

    const startDate = newRange.start.toString();
    const endDate = newRange.end.toString();

    router.get(
        route("home"),
        {
            start_date: startDate,
            end_date: endDate,
        },
        {
            preserveState: false,
            preserveScroll: true,
        },
    );
};

const totalPass = computed(() => {
    var total = 0;

    props.activeDomains.forEach(
        (value) => (total += getCurrentReport(value).pass),
    );

    return total;
});

const totalFail = computed(() => {
    var total = 0;

    props.activeDomains.forEach(
        (value) => (total += getCurrentReport(value).fail),
    );

    return total;
});

const totalPassBefore = computed(() => {
    let total = 0;
    props.activeDomains.forEach((d) => (total += getReportBefore(d).pass));
    return total;
});

const totalFailBefore = computed(() => {
    let total = 0;
    props.activeDomains.forEach((d) => (total += getReportBefore(d).fail));
    return total;
});

const avgCompliance = computed(() => {
    return Number((props.statistics?.complianceRate ?? 0).toFixed(1));
});

const avgNonCompliance = computed(() => {
    var total = 0;

    props.activeDomains.forEach(
        (value) => (total += getCurrentReport(value).fail),
    );

    return (
        Math.round(
            (totalFail.value / (totalPass.value + totalFail.value)) * 1000,
        ) / 10
    );
});

const nonePoliciesDomains = computed(() => {
    return props.activeDomains.filter(
        (domain) =>
            !["reject", "quarantine", "", null].includes(domain.dmarcPolicy),
    );
});

const noneDmarcRecordDomains = computed(() => {
    return props.activeDomains.filter(
        (domain) =>
            domain.report_current.pass === 0 &&
            domain.report_current.fail === 0,
    );
});

const daysLeftToTrial = (createdAt) => {
    let createdAtTimestamp = new Date(createdAt).getTime();

    if (createdAtTimestamp < 1712231594000) {
        createdAtTimestamp = 1712231594000;
    }

    let currentTimestamp = Date.now();

    let differenceInDays =
        (currentTimestamp - createdAtTimestamp) / (1000 * 60 * 60 * 24);

    if (differenceInDays < 0) {
        differenceInDays = 0;
    }

    return 30 - Math.floor(differenceInDays);
};

const calculateCompliance = (pass, fail) => {
    return pass + fail > 0
        ? Math.floor((pass / (pass + fail)) * 100) + "%"
        : 0 + "%";
};

const calculateColorByPercents = (percents) => {
    var percentNum = parseInt(percents.replace("%", ""));

    if (percentNum > 75) {
        return "#00c951";
    }

    if (percentNum > 50) {
        return "#f0b100";
    }

    return "#fb2c36";
};

const fetchAiAssesment = async () => {
    try {
        const res = await axios.get(route("dashboard.ai"));
        aiAssesment.value = res.data;

        if (res.data.status === "processing") {
            setTimeout(fetchAiAssesment, 5000);
        }
    } catch (e) {
        console.error(e);
    }
};

const page = usePage<IRhizomPageProps>();

const canAddDomain = computed(() => {
    const hasBasicPlan = page.props.hasBasicPlan;
    const hasScalePlan = page.props.hasScalePlan;
    const domainCount = page.props.totalDomainsCount;
    const hasGodMode = page.props.hasGodMode;

    if (hasGodMode) {
        return true;
    }

    if (hasBasicPlan && !hasScalePlan) {
        return domainCount < 1;
    }

    if (hasScalePlan) {
        return domainCount < 50;
    }

    return page.props.isTrialing;
});

const createDomainModal = ref(false);
const domainLimitMessage = ref("");
const showDomainLimitModal = ref(false);

const closeDomainLimitModal = () => {
    showDomainLimitModal.value = false;
    domainLimitMessage.value = "";
};

const closeCreateDomainModal = () => {
    createDomainModal.value = false;
    router.visit(route("home"), {
        method: "get",
        preserveState: false,
        preserveScroll: false,
    });
};

const handleAddDomain = () => {
    if (canAddDomain.value) {
        createDomainModal.value = true;
    } else {
        // Show domain limit modal with appropriate message
        const hasBasicPlan = page.props.hasBasicPlan;
        const hasScalePlan = page.props.hasScalePlan;
        const isTrialing = page.props.isTrialing;
        const hasGodMode = page.props.hasGodMode;

        if (hasGodMode) {
            return;
        }

        if (hasBasicPlan && !hasScalePlan) {
            domainLimitMessage.value = t(
                "vue.cannot_add_more_domains_basic_plan",
            );
        } else if (hasScalePlan) {
            domainLimitMessage.value = t(
                "vue.cannot_add_more_domains_scale_plan",
            );
        } else if (!isTrialing) {
            domainLimitMessage.value = t(
                "vue.cannot_add_more_domains_upgrade_required",
            );
        }

        showDomainLimitModal.value = true;
    }
};

onMounted(fetchAiAssesment);
</script>
